import { Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivityHotnessService } from '../../../../data-services/activity-hotness.service';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class CreateBusinessActivityService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private translateService: TranslateService,
    private sanitizer: DomSanitizer,
    private hotnessService: ActivityHotnessService,
    private matIconRegistry: MatIconRegistry,
  ) {}

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getFlameString(activityType: string, textKey: string): Observable<string> {
    const hotness = this.hotnessService.getHotness(activityType);
    const label = hotness?.label || 'AUTOMATIONS.EDITOR.TASKS.CREATE_BUSINESS_ACTIVITY.HOTNESS_RATING.OPTIONS.NO_FLAME';
    const flames = hotness?.flames || [];
    const buildSVGIcon = (html: any) =>
      `<div class="mat-icon" style="color:#e53935; vertical-align: middle; width: 18px; padding-bottom: 5px;">${html}</div>`;
    return combineLatest([
      this.translateService.stream(textKey),
      this.translateService.stream(label),
      this.matIconRegistry.getNamedSvgIcon('hotness'),
    ]).pipe(
      map(([text, flameLabel, icon]) => {
        text += `<b>${flameLabel}</b>`;
        flames.forEach((_) => (text += buildSVGIcon(icon.outerHTML)));
        return text;
      }),
    );
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const stepData = activity?.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    return this.getFlameString(
      stepData.activity_type,
      'AUTOMATIONS.EDITOR.TASKS.CREATE_BUSINESS_ACTIVITY.CREATED_BUSINESS_ACTIVITY',
    );
  }

  getTemplateActionSubtitle(namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitle(namespace, step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};
    if (data) {
      return this.getFlameString(data.activity_type, 'AUTOMATIONS.EDITOR.TASKS.CREATE_BUSINESS_ACTIVITY.ACTION');
    }
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_BUSINESS_ACTIVITY.CREATE_BUSINESS_ACTIVITY');
  }

  getActionSubtitleSuccessState(_data: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_data: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return of('');
  }
}
